import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import enTranslations from '../i18n/en.json';
import deTranslations from '../i18n/de.json';

const translations = {
  en: enTranslations,
  de: deTranslations
};

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  
  // Load language from localStorage on initialization
  const getInitialLanguage = () => {
    const savedLanguage = localStorage.getItem('app-language');
    return savedLanguage && translations[savedLanguage] ? savedLanguage : 'de';
  };

  const [currentLanguage, setCurrentLanguage] = useState(getInitialLanguage);

  const setLanguage = (lang) => {
    if (translations[lang]) {
      setCurrentLanguage(lang);
      localStorage.setItem('app-language', lang);
      // Also update i18next language
      if (i18n && i18n.changeLanguage) {
        i18n.changeLanguage(lang);
      }
    }
  };

  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = translations[currentLanguage];
    
    // Navigate through the nested object
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English if key not found in current language
        value = translations.en;
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            console.warn(`Translation key "${key}" not found`);
            return key; // Return the key itself if translation not found
          }
        }
        break;
      }
    }
    
    // If value is a string, replace parameters if any
    if (typeof value === 'string') {
      return value.replace(/\{\{(\w+)\}\}/g, (match, param) => {
        return params[param] !== undefined ? params[param] : match;
      });
    }
    
    return value;
  };

  const value = {
    currentLanguage,
    setLanguage,
    t,
    getCurrentLanguage: () => currentLanguage
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
